import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronRightIcon, PlusIcon } from "@heroicons/react/24/outline";
import type { CSSProperties } from "react";
import {
  ENABLED_LANGUAGE_SPACE_IDS,
  NATIVE_LANGUAGE_LABELS,
  TEXT_LANGUAGES,
} from "../../constants";
import {
  deriveEffectiveLanguageSpaceScope,
  getAddedLanguageSpacesStorageKey,
} from "../../hooks/useAddedLanguageSpaces";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useUserSettings } from "../../hooks/useUserSettings";
import useTranslation from "../../hooks/useTranslation";

export const Route = createFileRoute("/app/space_settings")({
  component: SpaceSettingsPage,
});

function SpaceSettingsPage() {
  const loginStatus = useLoginStatus();
  const userSettings = useUserSettings({
    enabled: loginStatus.data?.state === "logged in",
  });
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const storageKey = getAddedLanguageSpacesStorageKey(
    loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null,
  );
  const [localAddedLanguageSpaces] = useLocalStorage<string[]>(storageKey, []);
  const serverAddedLanguageSpaces = Array.isArray(
    userSettings.data?.added_language_spaces,
  )
    ? userSettings.data.added_language_spaces
    : [];
  const hasPersistedLocalAddedLanguageSpaces =
    typeof window !== "undefined" &&
    window.localStorage.getItem(storageKey) !== null;
  const resolvedLanguage = languageSpace === "ALL" ? "ENGLISH" : languageSpace;
  const { effectiveLanguageSpaceSet, hasAddedAllEnabledSpaces } =
    deriveEffectiveLanguageSpaceScope({
      addedLanguageSpaces:
        serverAddedLanguageSpaces.length > 0
          ? serverAddedLanguageSpaces
          : localAddedLanguageSpaces,
      enabledLanguageSpaceIds: ENABLED_LANGUAGE_SPACE_IDS,
      hasPersistedAddedLanguageSpaces:
        serverAddedLanguageSpaces.length > 0 ||
        hasPersistedLocalAddedLanguageSpaces,
      fallbackLanguageSpaceId: resolvedLanguage,
    });
  const visibleLanguageSpaces = TEXT_LANGUAGES.filter((language) =>
    effectiveLanguageSpaceSet.has(language.id),
  ).sort((a, b) => {
    if (a.id === resolvedLanguage) return -1;
    if (b.id === resolvedLanguage) return 1;
    return (NATIVE_LANGUAGE_LABELS[a.id] ?? a.pretty).localeCompare(
      NATIVE_LANGUAGE_LABELS[b.id] ?? b.pretty,
      undefined,
      { sensitivity: "base" },
    );
  });

  return (
    <main className="space-settings-page mx-auto w-full max-w-[38rem] px-5 pb-10 pt-8 sm:px-8 sm:pt-10">
      <header className="space-settings-heading">
        <h1>{translate("space_settings_title")}</h1>
        <p>{translate("space_settings_description")}</p>
      </header>
      <section className="mt-7" aria-label={translate("space_settings_title")}>
        <div className="space-settings-list">
          {visibleLanguageSpaces.map((space) => {
            const isCurrent = space.id === resolvedLanguage;
            return (
              <button
                key={space.id}
                type="button"
                data-testid={`space-settings-language-${space.id}`}
                aria-current={isCurrent ? "page" : undefined}
                onClick={() => {
                  setLanguageSpace(space.id);
                  void navigate({ to: "/app/space_settings" });
                }}
                className="space-settings-row"
              >
                <span
                  className="space-settings-book"
                  style={
                    {
                      "--space-book-color": space.accentBg,
                      "--space-book-ink": space.accentText,
                    } as CSSProperties
                  }
                  aria-hidden="true"
                />
                <span className="space-settings-language-name">
                  {NATIVE_LANGUAGE_LABELS[space.id] ?? space.pretty}
                </span>
                <span className="space-settings-code">{space.short}</span>
                {isCurrent ? (
                  <span
                    className="space-settings-current"
                    aria-label={translate("space_settings_active_badge")}
                  />
                ) : null}
                <ChevronRightIcon
                  className="space-settings-chevron"
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
        {!hasAddedAllEnabledSpaces ? (
          <button
            type="button"
            data-testid="space-settings-add-language-space"
            onClick={() => void navigate({ to: "/app/start-learning" })}
            className="space-settings-add"
          >
            <PlusIcon aria-hidden="true" />
            <span>{translate("space_settings_add_new_space")}</span>
          </button>
        ) : null}
      </section>
    </main>
  );
}
